/* eslint-disable max-len */
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

const drawerWidth = 320;

export const contrastBackgroundColor = '#333333';

type StyleProps = {
    drawerOpened?: boolean;
};

const useBasicStyle = makeStyles<Theme>((theme: Theme) =>
    createStyles({
        '@global': {
            body: {
                color: '#1C1C1C',
                backgroundColor: theme.palette.primary.contrastText,
                fontFamily: 'AudiTypeVar-Normal, Verdana, sans-serif',
            },
        },
        root: {
            minHeight: '100vh',
            backgroundColor: '#FFFFFF',
            display: 'flex',
            [theme.breakpoints.down('sm')]: {
                backgroundColor: '#fff',
                display: 'block',
            },
        },

        appBar: {
            zIndex: theme.zIndex.drawer + 1,
            width: `calc(100% - ${theme.spacing(12) + 1}px)`,
            transition: theme.transitions.create(['width', 'margin'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            backgroundColor: theme.palette.primary.contrastText,
            [theme.breakpoints.down('sm')]: {
                backgroundColor: contrastBackgroundColor,
                width: '0',
                height: theme.spacing(8),
            },
        },

        appBarShift: {
            marginLeft: drawerWidth,
            width: `calc(100% - ${drawerWidth}px)`,
            transition: theme.transitions.create(['width', 'margin'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
            [theme.breakpoints.down('sm')]: {
                marginLeft: 0,
                width: '100%',
            },
        },

        menuButton: {
            marginRight: 36,
        },

        hide: {
            display: 'none',
        },

        drawer: {
            width: drawerWidth,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            [theme.breakpoints.down('sm')]: {
                width: 0,
            },
        },

        drawerPaper: {
            backgroundColor: contrastBackgroundColor,
            [theme.breakpoints.up('sm')]: {
                borderRight: 0,
            },
            [theme.breakpoints.down('sm')]: {
                backgroundColor: '#fff',
            },
        },

        drawerOpen: {
            width: drawerWidth,
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
            [theme.breakpoints.down('sm')]: {
                width: 0,
            },
        },

        drawerClose: {
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            overflowX: 'hidden',
            width: theme.spacing(12) + 1,
            [theme.breakpoints.down('sm')]: {
                width: '100%',
            },
        },

        toolBar: {
            height: theme.spacing(10),
        },

        logoWrapper: (props?: StyleProps) => ({
            height: 80,
            backgroundColor: '#333333',
            padding: props?.drawerOpened ? '0px 0px 0px 40px' : 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: props?.drawerOpened ? 'start' : 'center',
        }),

        logo: {
            height: theme.spacing(5.25),
        },

        content: {
            flexGrow: 1,
            marginTop: theme.spacing(10),
            [theme.breakpoints.down('sm')]: {
                marginTop: theme.spacing(8),
            },
        },

        grayBackground: {
            backgroundColor: '#FFFFFF',
        },

        main: {
            padding: theme.spacing(3),
            paddingRight: theme.spacing(4),
            [theme.breakpoints.down('sm')]: {
                paddingRight: theme.spacing(3),
            },
        },

        mainDesktop: {
            padding: theme.spacing(3),
            paddingRight: theme.spacing(4),

            [theme.breakpoints.down('sm')]: {
                padding: 0,
            },
        },

        stickyHeader: {
            position: 'sticky',
            top: 80,
            zIndex: theme.zIndex.drawer - 1,
            display: 'flex',
            alignItems: 'center',
        },

        rightToolbar: {
            marginRight: theme.spacing(3),
        },

        divider: {
            backgroundColor: contrastBackgroundColor,
        },

        profileMenuButton: {
            marginLeft: 'auto',
        },

        profilePicture: {
            marginRight: theme.spacing(1.5),
        },

        profileMenuPaper: {
            width: theme.spacing(40),
            paddingTop: '0.75rem',
            boxShadow: 'none',
        },

        profileMenuList: {
            background: '#1C1C1C',
            paddingTop: '0 !important',
            paddingBottom: '0 !important',
            boxShadow: 'none',
            borderRadius: '0.5rem',
        },

        profileMenuItem: {
            color: '#ffffff !important',
            paddingBottom: '0.75rem !important',
            paddingTop: '0.75rem !important',
            paddingLeft: '1.5rem !important',
            '&:first-child': {
                borderRadius: '0.5rem 0 0 0',
            },
            '&:last-child': {
                backgroundColor: '#292929',
                marginLeft: '0',
                borderRadius: '0.5rem',
                justifyContent: 'center',
                '&:hover': {
                    backgroundColor: '#292929',
                },
            },
        },

        profileMenuIcon: {
            marginRight: '0.5rem',
        },

        selectedProfileMenuItem: {
            color: theme.palette.secondary.main,
            paddingLeft: '1.5rem !important',
        },

        backPaper: {
            height: theme.spacing(7),
            position: 'sticky',
            top: 80,
            zIndex: theme.zIndex.drawer - 1,
            [theme.breakpoints.down('sm')]: {
                display: 'none',
            },
        },

        lightBoxShadow: {
            boxShadow:
                '0px 2px 1px -1px rgb(0 0 0 / 0%), 0px 1px 1px 0px rgb(0 0 0 / 0%), 0px 1px 3px 0px rgb(0 0 0 / 6%)',
        },

        linkBack: {
            color: '#1C1C1C',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            height: '100%',
            marginLeft: theme.spacing(3),
            fontSize: '1rem',
            fontFamily: 'AudiTypeVar-Normal, Verdana, sans-serif !important',
        },

        title: {
            marginTop: theme.spacing(4),
            marginBottom: theme.spacing(5),
        },

        menuIcon: {
            zIndex: theme.zIndex.drawer + 1,
            color: '#333333',
            [theme.breakpoints.down('sm')]: {
                color: '#fff',
            },
        },

        profilePhoto: {
            width: `${theme.spacing(13)}px !important`,
            height: `${theme.spacing(13)}px !important`,
            [theme.breakpoints.down('sm')]: {
                position: 'absolute !important',
                top: '-50px',
                border: `${theme.spacing(0.5)}px solid ${theme.palette.secondary.contrastText}`,
            },
        },
    })
);
export default useBasicStyle;
